import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/Users/bradwestfall/Desktop/ReactTraining/Projects/monorepo/reacttraining.com/src/page-templates/BlogArticlePage.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`The next major version of React Router, version 6, is just around the corner. We
published the `}<a parentName="p" {...{
        "href": "https://github.com/ReactTraining/react-router/releases/tag/v6.0.0-alpha.0"
      }}>{`first alpha release back in
January`}</a>{`,
and we `}<a parentName="p" {...{
        "href": "https://github.com/ReactTraining/react-router/releases/tag/v6.0.0-alpha.3"
      }}>{`released another alpha last week`}</a>{`.`}</p>
    <p>{`For version 6, I'm using "alpha" to mean "not yet feature complete". I also
anticipate having some minor breaking changes between alphas as we nail down the
final APIs. For these reasons, I've been hesitant to publish anything more than
`}<a parentName="p" {...{
        "href": "https://github.com/ReactTraining/react-router/blob/dev/docs/advanced-guides/migrating-5-to-6.md"
      }}>{`a migration
guide`}</a>{`
and `}<a parentName="p" {...{
        "href": "https://github.com/ReactTraining/react-router/blob/dev/docs/installation/getting-started.md"
      }}>{`a getting started
guide`}</a>{`
for v6 up to this point because I didn't want to publish information about
something that we decided to change before the final release.`}</p>
    <p>{`But we're a bit further along now and I didn't want to cut too many releases
before writing up a proper blog post about v6, so here we are. My goal with this
post is to give you a few of the highlights from the v6 release and share with
you why I'm so excited about it.`}</p>
    <p>{`This is a pretty long post (lots to talk about!), so here's a quick summary:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#smaller-bundles"
        }}>{`Smaller Bundles`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#introducing-routes"
        }}>{`Introducing `}<code parentName="a" {...{
            "className": "language-text"
          }}>{`<Routes>`}</code></a><ul parentName="li">
          <li parentName="ul"><a parentName="li" {...{
              "href": "#relative-route-path-and-link-to"
            }}>{`Relative `}<code parentName="a" {...{
                "className": "language-text"
              }}>{`<Route path>`}</code>{` and `}<code parentName="a" {...{
                "className": "language-text"
              }}>{`<Link to>`}</code></a></li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "#nested-routes-and-layouts"
            }}>{`Nested Routes and Layouts`}</a></li>
        </ul></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#object-based-routes"
        }}>{`Object-based Routes`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#suspense-ready-navigation"
        }}>{`Suspense-ready Navigation`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#remaining-features"
        }}>{`Remaining Features`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#how-to-help"
        }}>{`How to Help`}</a></li>
    </ul>
    <h2 {...{
      "id": "overview"
    }}><a parentName="h2" {...{
        "href": "#overview",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Overview`}</h2>
    <p>{`Ryan and I have been working on React Router since May 2014 ... that's a long
time! We've been through a lot of iterations and ideas, and we've traveled
around the world and talked to literally thousands of React developers over the
last few years as part of our `}<a parentName="p" {...{
        "href": "https://reacttraining.com"
      }}>{`React workshops`}</a>{`.
React Router v6 takes the best of what we've learned over the years, combined
with what we know so far about where React is headed in the future, and delivers
it in a package that I believe is the best router we've ever built.`}</p>
    <p>{`Let's check it out.`}</p>
    <h2 {...{
      "id": "smaller-bundles"
    }}><a parentName="h2" {...{
        "href": "#smaller-bundles",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Smaller Bundles`}</h2>
    <p>{`One really quick thing right off the bat`}{`—`}{`React Router v6 is a `}<strong parentName="p">{`lot`}</strong>{` smaller
than its predecessor. For example, a quick high-level comparison of the bundles for
`}<a parentName="p" {...{
        "href": "https://bundlephobia.com/result?p=react-router-dom@5.1.2"
      }}><code parentName="a" {...{
          "className": "language-text"
        }}>{`react-router-dom@5.1.2`}</code></a>{`
vs.
`}<a parentName="p" {...{
        "href": "https://bundlephobia.com/result?p=react-router-dom@6.0.0-alpha.2"
      }}><code parentName="a" {...{
          "className": "language-text"
        }}>{`react-router-dom@6.0.0-alpha.2`}</code></a>{`
reveals the `}<strong parentName="p">{`total bundle size decreased by 70%`}</strong>{`. Smaller bundles means your app
loads more quickly, especially over slow/poor network connections.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "800px"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/ae12324448e8378c9090979a59269116/f43e4/v5-v6-bundle-size-comparison.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "35%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAA70lEQVQoz31RTW/DIAzl//+1nHKpuqk7tNshhGbq+CYD3rDTROnUzZIN2M9+thG1VvwlHGtKZynlQXPOD+9aC+MEGWstnHMwxsB7j9vXDS54zPN307kl1OYPjGGsddDOwHm3+SmPCgsyKSVYBjukVoDuIcRN146WWCNKM3LJ8CEgxtTyF3/OZelwLxSkAk/HvwvFY2vi2boEmRgjuq7D4XBkpmm6ou97DFLdCyyJSimcTm/sp26GYcDx5bWNHjZSsTJKKXGdPvlOOxlH1XapGbR2orXG+XyBNpZ9chxxef/g91bwv1/ej/obt1/LnvQHAjIjWqdcOicAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="a" {...{
            "className": "gatsby-resp-image-image",
            "alt": "v5 versus v6 bundle size comparison on Bundlephobia",
            "title": "v5 versus v6 bundle size comparison on Bundlephobia",
            "src": "/static/ae12324448e8378c9090979a59269116/5a190/v5-v6-bundle-size-comparison.png",
            "srcSet": ["/static/ae12324448e8378c9090979a59269116/772e8/v5-v6-bundle-size-comparison.png 200w", "/static/ae12324448e8378c9090979a59269116/e17e5/v5-v6-bundle-size-comparison.png 400w", "/static/ae12324448e8378c9090979a59269116/5a190/v5-v6-bundle-size-comparison.png 800w", "/static/ae12324448e8378c9090979a59269116/f43e4/v5-v6-bundle-size-comparison.png 1120w"],
            "sizes": "(max-width: 800px) 100vw, 800px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
  `}</a>{`
    `}</span></p>
    <p>{`You should also keep in mind that this is just a high-level comparison. React
Router contains many modules and hooks that you may not even need in your
application, so depending on which pieces you `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`import`}</code>{` the numbers will vary.`}</p>
    <p>{`There are `}<a parentName="p" {...{
        "href": "https://twitter.com/mjackson/status/1229156979714605056"
      }}>{`a few
reasons`}</a>{` for the big
change in our bundle size, and I plan on diving into them in a future post. But
for now, let's explore some of the API upgrades available in v6.`}</p>
    <h2 {...{
      "id": "introducing-routes"
    }}><a parentName="h2" {...{
        "href": "#introducing-routes",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Introducing `}<code parentName="h2" {...{
        "className": "language-text"
      }}>{`<Routes>`}</code></h2>
    <p>{`One of the most exciting changes in v6 is the powerful new `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Routes>`}</code>{` element.
This is a pretty significant upgrade from v5's `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Switch>`}</code>{` element with some
important new features including relative routing and linking, automatic route
ranking, and nested routes and layouts.`}</p>
    <h3 {...{
      "id": "relative-route-path-and-link-to"
    }}><a parentName="h3" {...{
        "href": "#relative-route-path-and-link-to",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Relative `}<code parentName="h3" {...{
        "className": "language-text"
      }}>{`<Route path>`}</code>{` and `}<code parentName="h3" {...{
        "className": "language-text"
      }}>{`<Link to>`}</code></h3>
    <p>{`Unlike the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Switch>`}</code>{` API in v5, all `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route path>`}</code>{` and `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Link to>`}</code>{` values under
v6's `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Routes>`}</code>{` element are automatically relative to the parent route that
rendered them. This makes it `}<strong parentName="p">{`a lot`}</strong>{` easier to think about your React Router
app as lots of small apps that are just stitched together at different "mount"
points.`}</p>
    <p>{`Pulling an example straight from `}<a parentName="p" {...{
        "href": "https://github.com/ReactTraining/react-router/blob/f59ee5488bc343cf3c957b7e0cc395ef5eb572d2/docs/advanced-guides/migrating-5-to-6.md#relative-routes-and-links"
      }}>{`the v5 to v6 migration
guide`}</a>{`,
a v6 app might look something like this:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "js"
    }}><pre parentName="div" {...{
        "className": "language-js"
      }}><code parentName="pre" {...{
          "className": "language-js"
        }}><span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` BrowserRouter`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` Routes`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` Route`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` Link `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react-router-dom'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`

`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`App`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`return`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`
    `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`BrowserRouter`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
      `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Routes`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
        `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Route path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`"/"`}</span>{` element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Home `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
        `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Route path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`"users/*"`}</span>{` element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Users `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
      `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`Routes`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
    `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`BrowserRouter`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{`

`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`Users`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`/* All <Route path> and <Link to> values in this
     component will automatically be "mounted" at the
     /users URL prefix since the <Users> element is only
     ever rendered when the URL matches /users/*
  */`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`return`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`
    `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`div`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
      `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`nav`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
        `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Link to`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`"me"`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`My Profile`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`Link`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
      `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`nav`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`

      `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Routes`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
        `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Route path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`"/"`}</span>{` element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`UsersIndex `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
        `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Route path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`":id"`}</span>{` element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`UserProfile `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
        `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Route path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`"me"`}</span>{` element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`OwnUserProfile `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
      `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`Routes`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
    `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`div`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span></code></pre></div>
    <p>{`This example probably looks like no big deal if you're learning about React
Router for the first time. It all seems very intuitive! But if you're used to
how things work in v5, you'll notice we fixed a few things:`}</p>
    <ul>
      <li parentName="ul">{`You don't need to use an `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`exact`}</code>{` prop on `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<Route path="/">`}</code>{` anymore. This is
because all `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<Route>`}</code>{` paths match exactly by default. If you want to match
more of the URL because you have child routes (see the `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<Routes>`}</code>{` defined in
the `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`Users`}</code>{` component above), use a trailing `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`*`}</code>{` as in `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<Route path="users/*">`}</code>{`.`}</li>
      <li parentName="ul">{`All routes and links automatically build upon the `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`path`}</code>{` of the route that
rendered them, so e.g. a `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<Link to="me">`}</code>{` that is rendered inside a `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<Route
path="users/*">`}</code>{` is going to link to `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`/users/me`}</code>{`.`}</li>
    </ul>
    <p>{`If you were doing relative routing and linking in React Router v5, you'll notice
you don't need to manually interpolate `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`match.path`}</code>{` and `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`match.url`}</code>{` anymore.`}</p>
    <h3 {...{
      "id": "nested-routes-and-layouts"
    }}><a parentName="h3" {...{
        "href": "#nested-routes-and-layouts",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Nested Routes and Layouts`}</h3>
    <p>{`In a large app it's nice to be able to spread out your route definitions across
multiple `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Routes>`}</code>{` elements so you can do code splitting more easily. But in a
smaller app, or with nested components that are closely related, you may want to
just see all of your routes in one place. This can help a lot with code
readability.`}</p>
    <p>{`Let's continue with the previous code example, except this time we'll combine
all of the routes into a single `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Routes>`}</code>{` declaration by taking the routes that
were defined in our `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`Users`}</code>{` component and hoisting them up into the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`App`}</code>{`. We'll
also use an `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Outlet>`}</code>{` in `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`Users`}</code>{` where the routes used to be to render the
child routes.`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "js"
    }}><pre parentName="div" {...{
        "className": "language-js"
      }}><code parentName="pre" {...{
          "className": "language-js"
        }}><span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  BrowserRouter`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
  Routes`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
  Route`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
  Link`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
  Outlet
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react-router-dom'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`

`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`App`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`return`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`
    `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`BrowserRouter`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
      `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Routes`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
        `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Route path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`"/"`}</span>{` element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Home `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
        `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Route path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`"users"`}</span>{` element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Users `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
          `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Route path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`"/"`}</span>{` element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`UsersIndex `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
          `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Route path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`":id"`}</span>{` element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`UserProfile `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
          `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Route path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`"me"`}</span>{` element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`OwnUserProfile `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
        `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`Route`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
      `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`Routes`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
    `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`BrowserRouter`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{`

`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`Users`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`return`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`
    `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`div`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
      `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`nav`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
        `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Link to`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`"me"`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`My Profile`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`Link`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
      `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`nav`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`

      `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Outlet `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
    `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`div`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span></code></pre></div>
    <p>{`If you compare this example with the previous one, you'll notice a few things:`}</p>
    <ul>
      <li parentName="ul">{`We're using `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<Route children>`}</code>{` to specify nested routes! The URL paths
nest along with the route elements, so `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`/users/me`}</code>{` renders
`}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<Users><OwnUserProfile /></Users>`}</code>{`.`}</li>
      <li parentName="ul">{`We used an `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<Outlet>`}</code>{` element as a placeholder. An `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<Outlet>`}</code>{` in this case is
how the `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`Users`}</code>{` component renders its child routes. So the `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<Outlet>`}</code>{` will
render either a `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<UserProfile>`}</code>{` or `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`<OwnUserProfile>`}</code>{` element depending on the
current location.`}</li>
    </ul>
    <p>{`One other small thing you may have noticed is that `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route path="users">`}</code>{` no
longer needs the trailing `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`/*`}</code>{` on the path. When the routes are all defined
together, you don't need it because the router can see all your routes at once.`}</p>
    <p><strong parentName="p">{`Note:`}</strong>{` The ability to nest routes like this was one of our favorite features
of React Router v3, but we lost it in v4 when we were more focused on the needs
of large apps, like code splitting. Version 6 brings together the best of both
worlds. Put all your `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Routes>`}</code>{` in one spot, or spread them out across your app
as needed. It's up to you!`}</p>
    <h2 {...{
      "id": "object-based-routes"
    }}><a parentName="h2" {...{
        "href": "#object-based-routes",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Object-based Routes`}</h2>
    <p>{`Up to this point we've talked about the new `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Routes>`}</code>{`, `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Route>`}</code>{`, and
`}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Outlet>`}</code>{` APIs which use React elements to declare your routes. I call this API
"the JSX API" for routing with React Router.`}</p>
    <p>{`But React Router v6 ships with another API for routing that uses plain JavaScript
objects to declare your routes. In fact, if you look at `}<a parentName="p" {...{
        "href": "https://github.com/ReactTraining/react-router/blob/f59ee5488bc343cf3c957b7e0cc395ef5eb572d2/packages/react-router/index.js#L234-L237"
      }}>{`the source of
`}<code parentName="a" {...{
          "className": "language-text"
        }}>{`<Routes>`}</code></a>{`,
you'll see that it's really just a tiny wrapper around a hook that is at the
heart of the router's matching algorithm: `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useRoutes`}</code>{`.`}</p>
    <p>{`The `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useRoutes`}</code>{` hook is a first-class API for routing that lets you declare and
compose your routes using JavaScript objects instead of React elements.
Continuing with the example above, let's see what it looks like with
`}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useRoutes`}</code>{`.`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "js"
    }}><pre parentName="div" {...{
        "className": "language-js"
      }}><code parentName="pre" {...{
          "className": "language-js"
        }}><span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  BrowserRouter`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
  Link`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
  Outlet`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
  useRoutes
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react-router-dom'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`

`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`App`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// We removed the <BrowserRouter> element from App because the`}</span>{`
  `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// useRoutes hook needs to be in the context of a <BrowserRouter>`}</span>{`
  `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// element. This is a common pattern with React Router apps that`}</span>{`
  `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// are rendered in different environments. To render an <App>,`}</span>{`
  `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// you'll need to wrap it in your own <BrowserRouter> element.`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`let`}</span>{` element `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`useRoutes`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`[`}</span>{`
    `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// A route object has the same properties as a <Route>`}</span>{`
    `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// element. The \`children\` is just an array of child routes.`}</span>{`
    `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'/'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Home `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
    `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
      path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'users'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
      element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Users `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
      children`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`[`}</span>{`
        `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'/'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`UsersIndex `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
        `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`':id'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`UserProfile `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
        `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'me'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` element`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`OwnUserProfile `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
      `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`]`}</span>{`
    `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`]`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`

  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`return`}</span>{` element`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{`

`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`Users`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`return`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`
    `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`div`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
      `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`nav`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
        `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Link to`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`"me"`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`My Profile`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`Link`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
      `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`nav`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`

      `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`Outlet `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
    `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`div`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span></code></pre></div>
    <p>{`The `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useRoutes`}</code>{` hook accepts a (possibly nested) array of JavaScript objects
that represent the available routes in your app. Each route has a `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`path`}</code>{`,
`}<code parentName="p" {...{
        "className": "language-text"
      }}>{`element`}</code>{`, and (optionally) `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`children`}</code>{`, which is just another array of routes.`}</p>
    <p>{`The object-based route configuration may look familiar if you were using the
`}<code parentName="p" {...{
        "className": "language-text"
      }}>{`react-router-config`}</code>{` package in v5. In v6, this configuration format has been
promoted to a first-class API in core and the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`react-router-config`}</code>{` package will
be deprecated.`}</p>
    <h2 {...{
      "id": "suspense-ready-navigation"
    }}><a parentName="h2" {...{
        "href": "#suspense-ready-navigation",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Suspense-ready Navigation`}</h2>
    <p>{`Version 6 is a great chance for us to get the router all ready for the future of
React: suspense. Instead of giving you access to the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`history`}</code>{` instance directly
(usage of which would introduce subtle bugs in a suspense-enabled app), v6 gives
you a `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`useNavigate`}</code>{` hook. This is useful any time you need to navigate
imperatively, e.g. after the user submits a form or clicks on a button.`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "js"
    }}><pre parentName="div" {...{
        "className": "language-js"
      }}><code parentName="pre" {...{
          "className": "language-js"
        }}><span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` React `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`
`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`import`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` useNavigate `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`from`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'react-router-dom'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`

`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`App`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`let`}</span>{` navigate `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`useNavigate`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`let`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`[`}</span>{`error`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` setError`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`]`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` React`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span><span parentName="code" {...{
            "className": "token function"
          }}>{`useState`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token keyword"
          }}>{`null`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`

  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`async`}</span>{` `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`handleSubmit`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token parameter"
          }}>{`event`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
    event`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span><span parentName="code" {...{
            "className": "token function"
          }}>{`preventDefault`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`
    `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`let`}</span>{` result `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`await`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`submitForm`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`event`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span>{`target`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`
    `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`if`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`result`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span>{`error`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
      `}<span parentName="code" {...{
            "className": "token function"
          }}>{`setError`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`result`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span>{`error`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`
    `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`else`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
      `}<span parentName="code" {...{
            "className": "token function"
          }}>{`navigate`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`'success'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`
    `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{`

  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`return`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span>{`
    `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span>{`form onSubmit`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`handleSubmit`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
      `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// ...`}</span>{`
    `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`<`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`/`}</span>{`form`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`>`}</span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`;`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span></code></pre></div>
    <p>{`If you need to do a replace instead of a push, use `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`navigate('success', {
replace: true })`}</code>{`. If you need state, use `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`navigate('success', { state })`}</code>{`.`}</p>
    <p>{`In a suspense-enabled app, the `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`navigate`}</code>{` function is aware of when your app is
suspending. When it is, any push navigation is `}<a parentName="p" {...{
        "href": "https://github.com/ReactTraining/react-router/blob/f59ee5488bc343cf3c957b7e0cc395ef5eb572d2/packages/react-router/index.js#L399-L403"
      }}>{`automatically converted into a
replace`}</a>{`
action. This might be useful when building a single-page app with multiple
links, and the user clicks on a link that suspends because it needs to load
data, but then quickly clicks on another link and goes to a new page. In this
situation the router would automatically omit the first navigation from the
browser history because that page never actually loaded.`}</p>
    <p>{`Of course, the declarative way to navigate around is by putting `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Link>`}</code>{`s on the
page. `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`<Link>`}</code>{` `}<a parentName="p" {...{
        "href": "https://github.com/ReactTraining/react-router/blob/f59ee5488bc343cf3c957b7e0cc395ef5eb572d2/packages/react-router-dom/index.js#L173"
      }}>{`uses `}<code parentName="a" {...{
          "className": "language-text"
        }}>{`navigate`}</code>{` under the
hood`}</a>{`
to update the URL, so all links will be ready for suspense.`}</p>
    <h2 {...{
      "id": "remaining-features"
    }}><a parentName="h2" {...{
        "href": "#remaining-features",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Remaining Features`}</h2>
    <p>{`There is a LOT more in this release than I can cover in a single blog post, but
the features I've already mentioned are definitely the highlights. I'll be
posting more about some of the other things you can do with v6 very soon.`}</p>
    <p>{`There are still two more major features I wanna deliver before moving into beta
releases: preloading and focus management. We already have some fairly concrete
ideas around how these are going to work, and we will have examples soon about
how to use both of these features as soon as they are merged into `}<a parentName="p" {...{
        "href": "https://github.com/ReactTraining/react-router/tree/dev"
      }}>{`the `}<code parentName="a" {...{
          "className": "language-text"
        }}>{`dev`}</code>{`
branch`}</a>{` where all v6
development is happening.`}</p>
    <h2 {...{
      "id": "how-to-help"
    }}><a parentName="h2" {...{
        "href": "#how-to-help",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`How to Help`}</h2>
    <p>{`Probably the best thing you can do to help us get v6 across the finish line is
to use it and let us know what you think! You can find instructions about how to
get started in `}<a parentName="p" {...{
        "href": "https://github.com/ReactTraining/react-router/blob/f59ee5488bc343cf3c957b7e0cc395ef5eb572d2/docs/installation/getting-started.md"
      }}>{`the Getting Started
guide`}</a>{`.`}</p>
    <p>{`If you already have an app on v5, you can follow the guidelines `}<a parentName="p" {...{
        "href": "https://github.com/ReactTraining/react-router/blob/dev/docs/advanced-guides/migrating-5-to-6.md"
      }}>{`in the
migration
guide`}</a>{`
and let us know what snags you run into. Although there are a few breaking
changes from v4/5, we did not lose any functionality, only gained some. So
everything you were doing before is possible in the new architecture, and quite
a few things should be easier!`}</p>
    <p>{`One of the other major ways you can contribute to the project is through
documentation. `}<a parentName="p" {...{
        "href": "https://twitter.com/mjackson/status/1177099501422538753"
      }}>{`Good docs are absolutely essential to the success of any open
source project`}</a>{`. There
are a few different ways you can help us with our docs:`}</p>
    <ul>
      <li parentName="ul">{`Read through the current docs and tell us if something doesn't flow, there's a
typo, or something is unclear`}</li>
      <li parentName="ul">{`Look for TODOs and submit a pull request (PR)`}</li>
    </ul>
    <p>{`If you're interested in `}<a parentName="p" {...{
        "href": "https://help.github.com/en/github/collaborating-with-issues-and-pull-requests/creating-a-pull-request"
      }}>{`submitting a
PR`}</a>{`,
please read through `}<a parentName="p" {...{
        "href": "https://github.com/ReactTraining/react-router/blob/dev/CONTRIBUTING.md"
      }}>{`the contributing
guidelines`}</a>{`
first. They'll help you get all setup.`}</p>
    <p>{`Hopefully this post has given you some insight into the new features of React
Router v6 and what we're hoping to accomplish with it. I hope it has also given
you some ideas about how you can get started with it and use it in your app if
you'd like.`}</p>
    <p>{`We have already started building everything that we do on top of v6 and we have
received very positive feedback from several early alpha testers. It shouldn't
be too long before we get a final release out the door.`}</p>
    <p>{`Enjoy, and we'll post more updates soon!`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      